import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Megaphone } from "lucide-react";
import logoAsset from "@/assets/balham-babes-logo.svg.asset.json";
import { isMedianApp } from "@/lib/medianOneSignal";
import { AREA_HUBS, CATEGORY_HUBS } from "@/lib/areaHubs";

import { Button } from "@/components/ui/button";

const COMMUNITY_LINKS = [
  { to: "/activities", label: "What's On" },
  { to: "/community", label: "Community Wall" },
  { to: "/playdates", label: "Playdates" },
  { to: "/noticeboard", label: "Noticeboard" },
  { to: "/local-spotlights", label: "Local Spotlights" },
  { to: "/meet-local-parents-balham", label: "Meet local parents" },
];

const ABOUT_LINKS = [
  { to: "/cecile-serena-story", label: "Cecile & Serena Story" },
  { to: "/contact", label: "Contact Us" },
  { to: "/help", label: "Help & Support" },
  { to: "/advertise", label: "Advertise With Us" },
];

const LEGAL_LINKS = [
  { to: "/community-rules", label: "Community Rules" },
  { to: "/terms-of-service", label: "Terms of Service" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
];

const Column = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
      {title}
    </p>
    <nav className="flex flex-col gap-2 text-sm">{children}</nav>
  </div>
);

const columnLink =
  "text-muted-foreground hover:text-foreground transition-colors w-fit";

export const Footer = () => {
  return (
    <footer className="hidden md:block border-t border-border/50 bg-muted/30 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Brand + primary actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-8 border-b border-border/50">
          <div className="flex flex-col items-start gap-3">
            <img src={logoAsset.url} alt="Balham Babes" className="h-8 w-auto" />
            <p className="text-sm text-muted-foreground max-w-sm">
              Connecting local parents and carers with activities and community
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="sm">
              <a
                href="https://forms.gle/c2CkPKuBFrbY7zpP7"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                Share Feedback
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/advertise" className="flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                Advertise With Us
              </Link>
            </Button>
          </div>
        </div>

        {/* Link columns — discovery/SEO links kept apart from everything else */}
        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4 py-10">
          <Column title="Activities by area">
            {AREA_HUBS.map((hub) => (
              <Link
                key={hub.slug}
                to={`/activities/${hub.slug}`}
                className={columnLink}
              >
                Activities in {hub.name}
              </Link>
            ))}
          </Column>

          <Column title="Browse by category">
            {CATEGORY_HUBS.map((category) => (
              <Link
                key={category.slug}
                to={`/activities/balham/${category.slug}`}
                className={columnLink}
              >
                {category.label} in Balham
              </Link>
            ))}
          </Column>

          <Column title="Community">
            {COMMUNITY_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className={columnLink}>
                {link.label}
              </Link>
            ))}
          </Column>

          <Column title="About & support">
            {ABOUT_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className={columnLink}>
                {link.label}
              </Link>
            ))}
          </Column>
        </div>

        {/* Partners */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-8 border-t border-border/50">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            Partners
          </p>
          <Link
            to="/partners/fox-and-cubs"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Fox &amp; Cubs Childcare
          </Link>
        </div>

        {/* PWA Prompt — web only, never inside the native app */}
        {!isMedianApp() && (
          <div className="bg-primary/5 rounded-lg p-4 text-center">
            <p className="text-sm text-foreground font-medium mb-1">
              📱 Get the full app experience
            </p>
            <p className="text-xs text-muted-foreground">
              Add Balham Babes to your home screen for instant access and push
              notifications when friends join activities!
            </p>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-border/50 mt-10" />

        {/* Copyright + legal */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            © 2026 Cherche la Beaute UK LTD. All rights reserved.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
            {LEGAL_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className={columnLink}>
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};
